import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { crmCampaignsAPI } from '../../services/api';
import {
  DeleteDialog, EditableTable, FieldLabel, SingleGamesSection,
  FREESPIN_COUNT_HINT, apiError, isFreespinCount, isValidAmount, useEditableList,
} from './campaignShared';

// Second Chance Freespins: its one games row, and deposit tiers. The limits table is keyed by
// A/B test group and currency, but the campaign uses one of each (A, USD); the backend fills them
// in and the page only names the currency.
//
// Each tier is edited as its min deposit alone, like Wednesday's: the backend derives every upper
// end from the next tier's min, so the table never shows the same amount twice.
function LimitFormDialog({ open, initial, currency, onClose, onSaved }) {
  const editing = Boolean(initial);
  const [from, setFrom] = useState('');
  const [freespins, setFreespins] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setFrom(initial?.deposit_amount_from ?? '');
    setFreespins(initial ? String(initial.freespin_number) : '');
    setError('');
  }, [open, initial]);

  const freespinsBad = freespins.trim() !== '' && !isFreespinCount(freespins);
  const valid = isValidAmount(from) && isFreespinCount(freespins);

  const submit = async () => {
    setSaving(true);
    setError('');
    const body = { deposit_amount_from: from.trim(), freespin_number: Number(freespins) };
    try {
      if (editing) {
        await crmCampaignsAPI.updateSecondChanceLimit(initial.deposit_amount_from, body);
      } else {
        await crmCampaignsAPI.createSecondChanceLimit(body);
      }
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Failed to save tier.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit deposit tier' : 'Add deposit tier'}</DialogTitle>
          <DialogDescription>
            Amounts are in {currency}. A tier applies from its min deposit up to the next tier's; the
            highest tier has no upper limit. Each tier must give more freespins than the one below it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1.5 block">
              <FieldLabel>Min deposit</FieldLabel>
              <Input inputMode="decimal" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="space-y-1.5 block">
              <FieldLabel>Freespin count</FieldLabel>
              <Input inputMode="numeric" value={freespins} onChange={(e) => setFreespins(e.target.value)} />
            </label>
          </div>
          {freespinsBad && <p className="text-xs font-medium text-negative">{FREESPIN_COUNT_HINT}</p>}
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add tier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LIMIT_COLUMNS = [
  { label: 'Min deposit', className: 'text-right', render: (r) => r.deposit_amount_from },
  { label: 'Freespin count', className: 'text-right', render: (r) => r.freespin_number },
];

function LimitsSection() {
  const list = useEditableList(crmCampaignsAPI.listSecondChanceLimits, 'limits');
  const currency = list.data?.currency || 'USD';
  const d = list.deleting;
  return (
    <>
      <EditableTable
        defaultCollapsed title={`Deposit tiers (${currency})`}
        description={
          'Freespins a player gets, based on their total deposits the previous day. A tier applies from its min deposit up to the next tier\'s.'
          + ' A higher tier must give more freespins than the one below it.'
          + (list.data?.irregular ? ' The stored tiers have gaps, a capped top tier or freespins that don\'t increase; saving fixes the ranges, and freespins need editing.' : '')
        }
        addLabel="Add tier" emptyText="No deposit tiers yet."
        loading={list.loading} error={list.error} rows={list.rows} columns={LIMIT_COLUMNS}
        rowKey={(r) => r.deposit_amount_from}
        onAdd={list.openAdd} onEdit={list.openEdit} onDelete={list.setDeleting}
      />
      <LimitFormDialog
        open={list.form.open} initial={list.form.row} currency={currency} onClose={list.closeForm}
        onSaved={() => { list.closeForm(); list.load(); }}
      />
      <DeleteDialog
        open={Boolean(d)} title="Delete deposit tier?"
        description={d ? `The tier from ${d.deposit_amount_from} ${currency}; the tier below it will reach up to the next one.` : ''}
        onConfirm={() => crmCampaignsAPI.deleteSecondChanceLimit(d.deposit_amount_from)}
        onClose={() => list.setDeleting(null)}
        onDeleted={() => { list.setDeleting(null); list.load(); }}
      />
    </>
  );
}

const SecondChanceFreespinsPanel = () => (
  <div className="space-y-6">
    <SingleGamesSection
      load={crmCampaignsAPI.getSecondChanceGames} save={crmCampaignsAPI.setSecondChanceGames} />
    <LimitsSection />
  </div>
);

export default SecondChanceFreespinsPanel;
